<template>
	<div>
		<div class="offer-list-container">
			<h2 class="offer-list__title offer-list__title--container">{{ translate('offers.title') }}</h2>
		</div>
		<ListPageSearch
			ref="listPageSearch"
			:has-sort="false"
			list-type="offer"
		/>
		<article
			v-if="storeInitialized"
			id="offerlist"
			class="offers"
		>
			<aside
				v-if="isDesktop"
				class="offers__aside"
			>
				<ListFilters
					is-hotel-page
					list-type="offer"
					class="offer-list__offer-filter"
					:tour-operator-loading="tourOperatorLoading"
				/>
			</aside>
			<section class="offers__content">
				<filter-tag-list
					:filter-number="filtersCount"
					class="offers__filter-tag-list"
				/>
				<BestPriceCalendarContainer :offer-list-is-loading="loading" />
				<div
					ref="offerList"
					class="offers__list"
				>
					<h3 class="offer-list__title offer-list__title--section">
						{{ formData.onlyHotel ? translate('offers.hotelOffers') : translate('offers.packageDeals') }}
					</h3>
					<UpsellingHotelList v-if="isDesktop" />
					<NoOffersFound
						v-if="!items.length && !loading"
						class="offers__no-offers-found"
						@OpenFilters="openFilters"
						@OpenSearch="openSearch"
					/>

					<OfferListItemList
						class="offers__item-list"
						:items="items"
						:loading="loading"
						:client="client"
						:hotel-data="hotelData"
						@OfferListItem:FetchDetails="fetchItemDetails"
					/>
				</div>
				<div class="offers__pager">
					<rounded-button
						v-if="links && links.next && !loadMore && !loading && items.length"
						class="offers__more-button"
						theme="outlined"
						@click.prevent="() => getMoreItems()"
					>
						Mehr Angebote laden
					</rounded-button>

					<Loading
						v-if="loadMore"
						size="medium"
						class="offers__loader"
					/>

					<a
						v-if="items.length && !loading"
						href="#"
						class="offers__scroll-top"
						aria-label="Scroll nach oben"
						@click.prevent="scrollTop"
					>
						<span class="offers__scroll-top-label">nach oben</span>
						<svg
							class="offers__scroll-top-icon"
							viewBox="0 0 15 32"
						>
							<path
								d="M0.438 27.125l11.125-11.125-11.125-11.125c-0.625-0.625-0.625-1.563 0-2.188 0.563-0.625 1.563-0.625 2.188 0l12.188 12.25c0.625 0.625 0.625 1.5 0 2.125l-12.188 12.25c-0.625 0.625-1.625 0.625-2.188 0-0.625-0.625-0.625-1.563 0-2.188z"
							/>
						</svg>
					</a>
				</div>
			</section>
			<WelcomeBackModal
				ref="welcomeBackModal"
				@SmallModal:Close="getItems"
			/>
		</article>
	</div>
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { AxiosError } from 'axios';
import axios from 'axios';
import request, { cancelRequestIncludesPattern } from '@components/common/services/request';
import { getAll as getAllParams, getParam, updateURL } from '@components/common/services/url';
import { updateBoardTypePrices } from '@/components/common/services/bestOfferBoardService';
import { throttle, scrollIt } from '@utils/utils';
import { determineClient, getBaseUrl } from '@utils/environmentUtils';
import { formToParams, offerItemDecor } from '@components/common/services/transform';
import OfferListItemList from '@lmt-rpb/OfferListItemList/OfferListItemList.vue';
import ListFilters from '@lmt-rpb/ListFilters/ListFilters.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import type { OfferResponseData, MergedOfferItemData, MergedOfferResponseData } from '@interfaces//offer';
import { EventBus } from '@global-js/event-bus';
import NoOffersFound from '@lmt-rpb/NoOffersFound/NoOffersFound.vue';
import FilterTagList from '@lmt-rpb/FilterTagList/FilterTagList.vue';
import ViewHotelAnalytics from '@/js/modules/analytics/ViewHotelAnalytics';
import { capitalize, computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from '@/components/common/store';
import * as searchHistoryService from '@components/common/services/localStorage/searchHistoryService';
import { addToLastSeenHotelsPersistence } from '@services/lastSeenHotelService';
import UpsellingHotelList from '@lmt-rpb/UpsellingHotelpage/UpsellingHotelpage.vue';
import type { OfferType } from '@/interfaces/hotel-list-types/hotel-list-types';
import ListPageSearch from '@lmt-rpb/ListPageSearch/ListPageSearch.vue';
import type { SearchFormDataType } from '@interfaces/search-form';
import WelcomeBackModal from '@lmt-rpb/WelcomeBackModal/WelcomeBackModal.vue';
import type { OfferListTypoHotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import { fetchTourOperatorInventory } from '@services/tourOperatorInventoryService';
import { verifyOffer } from '@services/verifyOfferService';
import BestPriceCalendarContainer from './BestPriceCalendarContainer.vue';
import type { ApiDataTypesKeys } from '../common/types';
import { updateRoomTypePrices } from '@services/bestOfferRoomService';
import { useSearchMaskUpdate } from '@/js/composables/searchMaskUpdate';
import { API_URLS, NUMBER_OF_OFFER_RESULTS } from '@global-js/constants';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import { useTranslations } from '@helper/composables/translations';
import { useFilters } from 'src/composables/useFIlters';

const { translate } = useTranslations();
const store = useStore();
const REQUEST_CANCEL_TOKEN = 'itemDetails';

const listPageSearch = ref<InstanceType<typeof ListPageSearch>>();
const items = ref<MergedOfferItemData[]>([]);
const links = ref<{ prev?: string; next?: string }>({});
const welcomeBackModal = ref<InstanceType<typeof WelcomeBackModal>>();
const loading = ref(false);
const loadMore = ref(false);
const error = ref<AxiosError | null>(null);
const pager = ref(0);
const storeInitialized = ref(store.state.config.storeInitialized);
const offerList = ref<HTMLElement | null>(null);

const bestOfferType = defineModel<OfferType | null>('bestOfferType', { default: null });
const { isDesktop } = storeToRefs(useBreakpointStore());

const formData = computed((): SearchFormDataType => store.state.searchMask);

const savedFormData = ref({ ...formData.value }); // Copy of formData

const apiData = store.getters['searchMask/apiData'];

const tourOperatorLoading = computed((): boolean => store.state.tourOperatorLoading);

const client = determineClient(getBaseUrl());

const hotelData = computed(() => {
	const hotelDataJSON = document.getElementById('typo-data')?.dataset.hoteldata;
	let data: OfferListTypoHotelData;
	if (hotelDataJSON) {
		data = JSON.parse(hotelDataJSON);
	} else {
		throw new Error('Hoteldata not found in typo-data');
	}
	return data;
});

const isDebuggingAirportsMissing = computed((): boolean => getParam('debuggingMissingAirports') === '1');

const removeSessionItem = (): void => {
	try {
		sessionStorage.removeItem('lastfetch');
	} catch (errorRemoveItem) {
		console.log(errorRemoveItem);
	}
};

function openSearch() {
	if (!isDesktop.value) {
		listPageSearch.value!.searchFormModal!.openModal();
	}
}

function openFilters() {
	if (!isDesktop.value) {
		listPageSearch.value!.filterModal!.openModal();
	}
}

const formDataWasChanged = (): boolean => {
	// Excludes for offerlist update

	/* eslint-disable @typescript-eslint/no-unused-vars */
	const {
		destination: currentDestination,
		searchTerm: currentSearchTerm,
		modified: currentModified,
		maxTravelDurationError: currentError,
		board: currentBoard,
		room: currentRoom,
		...newFormData
	} = formData.value;
	const {
		destination: savedDestination,
		searchTerm: savedtSearchTerm,
		modified: savedModified,
		maxTravelDurationError: savedError,
		board: savedBoard,
		room: savedRoom,
		...newSavedFormData
	} = savedFormData.value;
	/* eslint-enable @typescript-eslint/no-unused-vars */

	// Update savedFormData
	savedFormData.value = { ...formData.value };

	// Check if wanted fields updated
	if (JSON.stringify(newFormData) !== JSON.stringify(newSavedFormData)) {
		return true;
	}

	return false;
};

const convertOfferResponseData = (offerResponseData: null | OfferResponseData) => {
	const type = formData.value.onlyHotel ? 'hotel' : 'package';
	const mergedOfferResponseData: MergedOfferResponseData = { Offers: [], _links: {} };

	if (offerResponseData && offerResponseData.Offers && offerResponseData.Offers.length) {
		mergedOfferResponseData.Offers = offerResponseData.Offers.map((obj) => offerItemDecor(obj, type));
	}

	if (offerResponseData && offerResponseData._links && Object.keys(offerResponseData._links).length) {
		mergedOfferResponseData._links = offerResponseData._links;
	}

	return mergedOfferResponseData;
};

const fetchItems = async (url?: string): Promise<MergedOfferResponseData | void> => {
	// determine which offer type to load
	const type = formData.value.onlyHotel ? 'hotel' : 'package';
	const exclude: ApiDataTypesKeys[] =
		type === 'hotel' ? ['DepartureAirport', 'DirectFlight', 'DepartureTime', 'ArrivalTime', 'DealsOnly'] : [];
	// Removing params which are at the moment specific to the hotellist request
	exclude.push(
		'Regions',
		'Cities',
		'MaxPrice',
		'SortOrder',
		'HotelCategory',
		'MinMeanRecommendationRate',
		'RatingAttributes',
		'HotelAttributes',
		'Countries'
	);
	const bodyParams = {
		numberOfResults: NUMBER_OF_OFFER_RESULTS,
	};
	const body = apiData(bodyParams, exclude);
	const fetchUrl = formData.value.onlyHotel ? API_URLS.OFFERS.HOTELS : API_URLS.OFFERS.PACKAGES;

	const uri = url || fetchUrl;

	return request<OfferResponseData>({ url: uri, data: body }, 'offerListOffers')
		.then(async (resp) => {
			const mergedOfferResponseData = convertOfferResponseData(resp);
			if (!resp.Offers?.[0]) return;
			bestOfferType.value = {
				TravelType: capitalize(type),
				...resp.Offers[0],
			} as unknown as OfferType;
			// remove error if any
			error.value = null;

			try {
				sessionStorage.lastfetch = Date.now();
			} catch (errorLastFetch) {
				console.log(errorLastFetch);
			}

			welcomeBackModal.value?.closeModal();
			return mergedOfferResponseData;
		})
		.catch((axiosError: AxiosError) => {
			const { response } = axiosError;

			if (response && response.status >= 500) {
				error.value = axiosError;
			} else {
				items.value = [];
				bestOfferType.value = null;
			}

			throw axiosError;
		});
};

const sendGa4EventViewItem = (offer: MergedOfferItemData): void => {
	const viewHotelAnalytics = ViewHotelAnalytics.createInstance(hotelData.value, offer);
	viewHotelAnalytics.fireViewItem();
};

const getItems = async (): Promise<void> => {
	loading.value = true;
	store.state.bestOfferLoading = true;
	let resp: MergedOfferResponseData | void;

	try {
		cancelRequestIncludesPattern(REQUEST_CANCEL_TOKEN);
		resp = await fetchItems();
		items.value = resp?.Offers || [];
		links.value = resp?._links || {};
		if (!resp || !resp.Offers.length) {
			pager.value = 0;
			// empty offer for reactivity purposes
			const emptyOffer: Partial<MergedOfferItemData> = {
				type: undefined,
				duration: undefined,
				BoardType: undefined,
				Room: undefined,
				CurrencyCode: undefined,
				Price: null,
			};
			// delete old bestOffer from store
			store.commit('updateBestOffer', emptyOffer);
		} else {
			store.commit('updateBestOffer', items.value[0]);
			sendGa4EventViewItem(items.value[0]);
		}
		addToLastSeenHotelsPersistence();

		loading.value = false;
	} catch (axiosError) {
		if (axios.isCancel(axiosError)) {
			loading.value = true;
			return;
		}
		loading.value = false;
	} finally {
		store.state.bestOfferLoading = false;
	}
};

const handleMobileSubmit = (): void => {
	if (formDataWasChanged()) {
		searchHistoryService.update();
		updateURL(formToParams(formData.value, 'offerList'));
		if (!window.location.href.includes('#offers')) {
			window.history.replaceState(window.history.state, '', window.location.href + '#offers');
		}
		getItems();
	}
};

// handlers
const handleResize = (event: Event): void => {
	EventBus.$emit('window:resize', event);
};

const handlePopState = ({ state }: PopStateEvent): void => {
	EventBus.$emit('window:popstate', state);
};

const handleKeydown = ({ which }: KeyboardEvent): void => {
	const meta = (
		{
			27: 'esc',
		} as { [key: number]: string }
	)[which];

	if (meta) {
		EventBus.$emit(`keydown.${meta}`);
	}
};

const fetchTouroperators = () => {
	const excludes: ApiDataTypesKeys[] = ['Regions', 'Cities', 'DealsOnly'];
	const bodyParams = {
		hotelId: formData.value.hotelId,
	};
	fetchTourOperatorInventory(bodyParams, excludes);
};

const fetchItemDetails = async (item: MergedOfferItemData, index: number): Promise<void> => {
	// The first offer should always cancel to stop a race condition if filters are switched too fast.
	const cancelTokenName = index === 0 ? REQUEST_CANCEL_TOKEN : `${REQUEST_CANCEL_TOKEN}-${item.OfferID}`;
	// @ts-ignore
	items.value[index].loading = true;

	// @ts-ignore
	items.value[index] = await verifyOffer(isDebuggingAirportsMissing, formData, item, cancelTokenName);

	items.value[index] = fallbackChildParam(items.value[index]);

	if (!items.value[index].available) {
		items.value[index].loading = false;
	}
};

const fallbackChildParam = (data: MergedOfferItemData) => {
	if (data.BookingURL) {
		const url = new URL(decodeURIComponent(data.BookingURL));
		const childParam = url.searchParams?.get('child');
		if (childParam?.includes(',')) {
			const modifiedChildParam = childParam.replaceAll(',', '-');
			url.searchParams.set('child', modifiedChildParam);
			data.BookingURL = decodeURIComponent(url.toString());
		}
	}
	return data;
};
const loadMoreAnalytics = () => {
	SelectContentAnalytics.selectContent({ cId: 'offerlist_button', cType: 'ux_hotelpage_load_more' });
};

const getMoreItems = (): void => {
	// set loading true
	loadMore.value = true;
	loadMoreAnalytics();

	fetchItems(links.value.next)
		.then((resp) => {
			// validate response
			if (!resp || !resp.Offers || Array.isArray(resp)) {
				items.value = [];
				links.value = {};
				pager.value = 0;

				return;
			}

			items.value.push(...resp.Offers);
			links.value = resp?._links || {};
		})
		.catch((axiosError: AxiosError) => {
			const { response } = axiosError;

			if (response && response.status >= 500) {
				error.value = axiosError;
			}
		})
		.finally(() => {
			// set loading false
			loadMore.value = false;
		});
};

const scrollTop = (): void => {
	scrollIt(0, 1000, 'easeOutQuad');
};

const { filtersCount } = useFilters();

useSearchMaskUpdate(updateBoardTypePrices, ['board', 'boardTypes']);
useSearchMaskUpdate(updateRoomTypePrices, ['room', 'roomTypes']);

watch(
	() => formData.value,
	() => {
		if (!isDesktop.value) {
			return;
		}

		if (formDataWasChanged()) {
			searchHistoryService.update();
			updateURL(formToParams(formData.value, 'offerList'));
			getItems();
		}
	},
	{ deep: true }
);

// life cycle events
onBeforeMount((): void => {
	throttle(handleResize, 250);

	// set window event listeners
	window.addEventListener('resize', handleResize);
	window.addEventListener('popstate', handlePopState);

	// set keydown listener
	document.addEventListener('keydown', handleKeydown);

	// update query params if page is loaded with params
	const params = getAllParams();
	if (Object.keys(params).length) {
		updateURL(formToParams(formData.value, 'offerList'));
	}

	// Remove sessionStorage timestamp 'lastfetch' before leaving the page
	window.addEventListener('beforeunload', removeSessionItem);
});

onMounted((): void => {
	// check if viewport is desktop

	fetchTouroperators();
	getItems();
	EventBus.$on('SearchMask:MobileSubmit', handleMobileSubmit);
});

onBeforeUnmount((): void => {
	window.removeEventListener('resize', handleResize);
	window.removeEventListener('popstate', handlePopState);
	document.removeEventListener('keydown', handleKeydown);
	EventBus.$off('SearchMask:MobileSubmit', handleMobileSubmit);

	window.removeEventListener('beforeunload', removeSessionItem);
});
</script>

<style lang="scss" scoped>
$scroll-color: $color-primary-l1;
$spacing-sm: 0.8rem;
$spacing-md: 1.6rem;
$spacing-lg: 3.2rem;

.page-container {
	padding-left: 2.66%;
	padding-right: 2.66%;
	max-width: $breakpoint-container;
	display: flex;
	flex-direction: column;
	margin-left: auto;
	margin-right: auto;
}

.offer-list__title {
	padding: 0;
	margin: 0;
	text-align: left;
	margin-top: $spacing-sm;
	font-size: $font-medium-3;
	line-height: 2.7rem;

	@media (min-width: $breakpoint-small) {
		font-size: $font-medium-2;
	}

	@media (min-width: $breakpoint-large) {
		font-size: $font-large-4;
	}

	&--section {
		font-family: $font-family-special;
		font-weight: $font-weight-semibold;
		margin-top: 2rem;

		@media screen and (max-width: $breakpoint-small) {
			font-size: $font-small-1;
		}

		@media screen and (min-width: $breakpoint-small) and (max-width: $breakpoint-medium) {
			margin-bottom: $spacing-md;
			font-size: $font-medium-3;
		}

		@media screen and (min-width: $breakpoint-medium) and (max-width: $breakpoint-large) {
			font-size: $font-medium-3;
		}

		@media screen and (min-width: $breakpoint-large) {
			margin-top: 0.8rem;
			font-size: $font-medium-2;
			margin-bottom: $spacing-md;
		}
	}

	&--container {
		@media screen and (max-width: $breakpoint-small) {
			margin-top: $spacing-md;
		}

		@media screen and (max-width: $breakpoint-large) {
			margin-bottom: $spacing-md;
		}
	}
}

@mixin responsive-offer-layout {
	@include media-query-up($breakpoint-large) {
		flex-direction: row;
		padding-bottom: $spacing-lg;

		.offers__content {
			width: 100%;
			margin-left: 0;
		}
	}

	@include media-query-up($breakpoint-extralarge) {
		padding-left: 0;
		padding-right: 0;

		.offers__aside {
			width: 25%;
			min-width: 35rem;
		}

		.offers__content {
			min-width: 72%;
			margin-left: 1.3rem;
		}
	}
}

.gallery-open .offers .offers__list {
	z-index: 100000000;
}

.offers {
	@extend .page-container;

	padding-bottom: $spacing-md;
	margin-top: 2rem;
	display: flex;
	flex-direction: column;

	.offers__order {
		margin-top: 1.5rem;
	}

	.offers__list-loader {
		width: 10rem;
		height: 10rem;
		margin: 30rem auto;
	}

	.offer-list__offer-filter {
		@include visible-from($breakpoint-large);
	}

	.offers__loader {
		margin: 0 auto;
	}

	&__list {
		display: flex;
		flex-direction: column;
		position: relative;
		z-index: 0;
	}

	&__item-list {
		margin-top: $spacing-md;

		@media screen and (min-width: $breakpoint-small) and (max-width: $breakpoint-extralarge) {
			margin-top: 0;
		}
	}

	&__filter-tag-list {
		margin-bottom: $spacing-lg;
	}

	&__no-offers-found {
		margin-top: 5.3rem;
	}

	&__more-button {
		display: block;
		margin: 0 auto;
	}

	&__pager {
		display: flex;
		align-items: center;
		position: relative;
		margin-top: calc(
			2.4rem - 2rem
		); /* Margin of list-item above is 2rem, total space between pager and list-item should be 2.4rem or 3.2rem (Desktop)*/

		@media (min-width: $breakpoint-extralarge) {
			margin-top: calc(3.2rem - 2rem);
		}
	}

	&__scroll-top {
		text-decoration: none;
		display: flex;
		align-items: center;
		gap: 0.8rem;
		position: absolute; /* Parent is relative. This is needed because this element shouldn't take up space */
		right: 0;
	}

	&__scroll-top-label {
		@include visible-from($breakpoint-extralarge, inline-block);
	}

	&__scroll-top-icon {
		width: 1.5rem;
		height: 1.5rem;
		transform: rotate(-90deg);
		fill: currentcolor;
		vertical-align: text-top;
	}

	@include responsive-offer-layout;
}

.offer-list-container {
	@extend .page-container;

	@media (max-width: $breakpoint-small) {
		margin-top: 0.4rem;
	}

	@include media-query-up($breakpoint-large) {
		flex-direction: row;
	}

	&__filter-tag-list {
		margin-bottom: 2.4rem;
	}

	@include media-query-up($breakpoint-extralarge) {
		padding-left: 0;
		padding-right: 0;
	}

	@media (max-width: $breakpoint-large) {
		margin-top: $spacing-md;
	}
}
</style>
